import { useState, useEffect, useCallback, useRef } from "react";

const EXAM_DATA = {
  title: "ISE Mock Exam 1",
  timeLimit: 150,
  passingScore: 60,
  elements: [
    { id: 1, name: "Managing Institutional Client Relationships", count: 16 },
    { id: 2, name: "Conflicts of Interest & Standards of Conduct", count: 8 },
    { id: 3, name: "Fixed Income", count: 12 },
    { id: 4, name: "Equities", count: 13 },
    { id: 5, name: "Securities Analysis & Investment Theory", count: 31 },
    { id: 6, name: "Managed & Other Products", count: 8 },
    { id: 7, name: "Execution & Market Integrity", count: 12 },
  ],
  questions: [
    // ===== ELEMENT 1: Managing Institutional Client Relationships (16 Qs) =====
    {
      id: 1, element: 1, difficulty: "Medium",
      question: "A Defined Benefit Pension Fund with significant payout obligations due in 30 years contacts an Investment Dealer seeking asset allocation advice. Which investment approach is most consistent with this client's primary constraint?",
      options: [
        "A. A high-frequency algorithmic trading strategy targeting short-term equity volatility",
        "B. Liability-Driven Investing (LDI) using long-duration bonds matched to the fund's future obligations",
        "C. A concentrated portfolio of high-growth technology stocks to maximize capital appreciation",
        "D. A leveraged ETF strategy tracking commodity futures for inflation protection"
      ],
      correct: 1,
      explanation: "Pension funds practice Liability-Driven Investing (LDI). Their primary goal is to match assets to liabilities by duration. A 30-year obligation requires long-duration bonds. Options A and C are speculative and inappropriate; D introduces leverage risk unsuitable for pension mandates."
    },
    {
      id: 2, element: 1, difficulty: "Hard",
      question: "A large Insurance Company with $500 million in surplus capital approaches your Investment Dealer for investment guidance. Given their regulatory constraints and business model of matching assets to policyholder claims, which portfolio allocation would be most appropriate?",
      options: [
        "A. 70% equities, 20% alternatives, 10% fixed income for maximum growth",
        "B. 85% investment-grade fixed income, 10% real estate, 5% cash equivalents",
        "C. 50% high-yield bonds, 30% private equity, 20% venture capital",
        "D. 60% commodities, 25% emerging market equities, 15% cryptocurrency"
      ],
      correct: 1,
      explanation: "Insurance companies are risk-averse with defined liabilities (paying death benefits/claims). They invest heavily in high-quality investment-grade fixed income to ensure capital preservation and liability matching. The allocation in B reflects their conservative mandate and regulatory requirements."
    },
    {
      id: 3, element: 1, difficulty: "Medium",
      question: "\"Red Leaf Capital,\" a boutique fund, wants to execute a Long/Short equity strategy using 2x leverage and needs to borrow securities for short selling. Which specific service unit within an Investment Dealer is designed to centralize these complex requirements?",
      options: [
        "A. The Equity Research department",
        "B. The Investment Banking division",
        "C. The Prime Brokerage unit",
        "D. The Retail Wealth Management desk"
      ],
      correct: 2,
      explanation: "Prime Brokerage is a specialized suite of services specifically designed for Hedge Funds. It centralizes custody, securities lending (for shorting), margin financing (leverage), and trade clearing — exactly the services Red Leaf Capital requires."
    },
    {
      id: 4, element: 1, difficulty: "Hard",
      question: "A Pension Fund wants to sell 500,000 shares of a thinly traded mining stock immediately without causing significant market impact. The Investment Dealer agrees to purchase the entire block using the firm's own capital at a negotiated discount. This transaction is best described as:",
      options: [
        "A. An agency trade executed through a dark pool",
        "B. A liability (principal) trade where the dealer assumes inventory risk",
        "C. A market-making transaction on the exchange order book",
        "D. A program trade using algorithmic execution"
      ],
      correct: 1,
      explanation: "In a liability (principal) trade, the dealer commits its own capital to purchase the block, providing immediate liquidity to the client. The dealer then assumes the risk of selling the shares gradually. This differs from an agency trade where the dealer merely connects buyers and sellers."
    },
    {
      id: 5, element: 1, difficulty: "Medium",
      question: "An Investment Dealer is onboarding a new institutional client — a Canadian Crown Corporation. Under CIRO's KYC regulations for institutional clients, which of the following is the dealer required to collect and verify?",
      options: [
        "A. The personal investment objectives and risk tolerance of the CEO",
        "B. The corporation's name, address, and authorized representatives verified with official documentation",
        "C. A detailed personal net worth statement of each Board member",
        "D. The corporation's complete customer list for due diligence purposes"
      ],
      correct: 1,
      explanation: "For institutional corporate clients, KYC requires collecting the entity's legal name, address, nature of business, and authorized representatives, verified through official documents such as certificates of incorporation or letters patent. Personal financial details of individual executives are not required for the entity-level KYC."
    },
    {
      id: 6, element: 1, difficulty: "Easy",
      question: "Which of the following best describes the primary difference between \"Buy-Side\" and \"Sell-Side\" participants in the institutional marketplace?",
      options: [
        "A. Buy-Side firms create financial products; Sell-Side firms invest capital for long-term growth",
        "B. Buy-Side firms invest and manage capital according to mandates; Sell-Side firms facilitate trading, research, and capital raising",
        "C. Buy-Side firms are unregulated; Sell-Side firms must register with CIRO",
        "D. Buy-Side and Sell-Side are interchangeable terms for all market participants"
      ],
      correct: 1,
      explanation: "The Buy-Side (pension funds, mutual funds, hedge funds) possesses capital and invests it according to specific mandates. The Sell-Side (Investment Dealers) manufactures products, provides liquidity, generates research, and facilitates trading — they earn money by serving the Buy-Side."
    },
    {
      id: 7, element: 1, difficulty: "Hard",
      question: "A fund manager opens an account with an Investment Dealer to trade Canadian equities. The fund manager is a registered entity regulated by a Canadian securities commission. How should the Investment Dealer handle suitability for this new institutional client?",
      options: [
        "A. Assess the fund manager's personal ability to judge risks before every individual trade",
        "B. The fund manager qualifies as a Permitted Client, so the dealer does not collect traditional suitability information",
        "C. Require a signed waiver exempting the fund from all suitability and KYC requirements",
        "D. Conduct a full retail-level suitability assessment including risk tolerance questionnaires"
      ],
      correct: 1,
      explanation: "Under CIRO rules, a registered fund manager qualifies as a Permitted Client and is presumed sophisticated enough to make their own investment decisions. The dealer does not collect traditional suitability information (risk tolerance, time horizon) as would be required for retail clients."
    },
    {
      id: 8, element: 1, difficulty: "Medium",
      question: "An Approved Person at an Investment Dealer receives a request from an institutional client to execute a complex derivatives trade. The Approved Person lacks the specific registration and expertise for this product. What is the most appropriate course of action?",
      options: [
        "A. Execute the trade anyway since the client is institutional and sophisticated",
        "B. Decline the request entirely and suggest the client find another dealer",
        "C. Direct the client to an internal subject-matter expert (SME) in the derivatives department",
        "D. Provide general information about derivatives and let the client decide independently"
      ],
      correct: 2,
      explanation: "When an Approved Person lacks the specific registration or expertise for a product, they must direct the client to an internal SME who has the proper qualifications. This ensures the client receives proper advice while maintaining regulatory compliance."
    },
    {
      id: 9, element: 1, difficulty: "Hard",
      question: "An Investment Dealer discovers during enhanced due diligence that an institutional client — a foreign hedge fund — has complex ownership structures involving entities in multiple jurisdictions. Several beneficial owners cannot be identified. Under Canada's Proceeds of Crime (Money Laundering) and Terrorist Financing Act, what must the dealer do?",
      options: [
        "A. Proceed with the relationship provided the hedge fund signs a risk acknowledgment",
        "B. Decline to open the account or freeze the relationship and consider filing a suspicious transaction report",
        "C. Accept the client after obtaining a letter from the hedge fund's legal counsel confirming compliance",
        "D. Open the account with enhanced monitoring and plan to identify beneficial owners within 12 months"
      ],
      correct: 1,
      explanation: "If beneficial ownership information cannot be obtained, the dealer must decline to open the account or freeze the existing relationship. The inability to identify beneficial owners is a fundamental AML compliance failure that cannot be mitigated by acknowledgments, legal letters, or monitoring plans."
    },
    {
      id: 10, element: 1, difficulty: "Medium",
      question: "A Registered Representative at an Investment Dealer publishes a social media post stating: \"Our firm's new equity fund guarantees 15% annual returns — invest now before spots run out!\" Which regulatory standard has been most clearly violated?",
      options: [
        "A. The requirement to maintain a discretionary trading agreement",
        "B. The standard requiring communications to be fair, honest, and balanced, and not misleading",
        "C. The requirement to file prospectus documents with securities commissions",
        "D. The prohibition against trading in grey-listed securities"
      ],
      correct: 1,
      explanation: "The communication violates the core standard that all client and public communications must be fair, honest, and balanced. Guaranteeing specific returns is misleading since no investment can guarantee returns. The urgency language ('before spots run out') creates undue pressure."
    },
    {
      id: 11, element: 1, difficulty: "Easy",
      question: "Which of the following correctly identifies the three primary ways Investment Dealers add value to institutional clients?",
      options: [
        "A. Tax planning, estate management, and insurance brokerage",
        "B. Liquidity provision, capital raising, and idea generation (research)",
        "C. Retail banking, mortgage lending, and credit card services",
        "D. Government lobbying, regulatory compliance, and public relations"
      ],
      correct: 1,
      explanation: "Investment Dealers serve institutional clients through three core functions: providing liquidity (enabling large trades without market impact), raising capital (underwriting IPOs and debt issues), and generating ideas (through research and corporate access to help clients generate alpha)."
    },
    {
      id: 12, element: 1, difficulty: "Hard",
      question: "A foreign institutional investor based in Singapore wishes to open an account with a Canadian Investment Dealer to purchase TSX-listed securities. What is the Approved Person's first obligation under the dealer's guidelines?",
      options: [
        "A. Open the account immediately and classify the investor as a retail client",
        "B. Consult the dealer's foreign-client onboarding policies and confirm jurisdictional restrictions before opening the account",
        "C. Decline the request as Canadian dealers cannot service non-resident investors",
        "D. Register the client as a politically exposed person regardless of their status"
      ],
      correct: 1,
      explanation: "Before servicing a foreign institutional client, the Approved Person must consult the dealer's foreign-client onboarding policies and confirm any jurisdictional restrictions. This includes cross-border regulatory requirements, local registration obligations, and any limitations on products or services available to non-resident investors."
    },
    {
      id: 13, element: 1, difficulty: "Medium",
      question: "Under CIRO rules, when opening a margin account for an institutional client, which of the following pieces of information is the Investment Dealer specifically required to collect?",
      options: [
        "A. The client's personal investment objectives and risk tolerance score",
        "B. The client's authorized trading representatives and signing officers",
        "C. A detailed breakdown of every security the client currently holds at other dealers",
        "D. The client's historical trading volume over the past five years"
      ],
      correct: 1,
      explanation: "For institutional margin accounts, CIRO requires collection of authorized representatives and signing officers — the individuals empowered to give trading instructions and bind the entity. Certain KYC elements like personal investment objectives and risk profiles are exempt for institutional clients."
    },
    {
      id: 14, element: 1, difficulty: "Medium",
      question: "An institutional client's Investment Policy Statement (IPS) prohibits holding any fixed-income securities rated below BBB-. The Investment Dealer recommends a BB+ rated corporate bond offering an attractive yield. What is the dealer's obligation?",
      options: [
        "A. Execute the trade since institutional clients are responsible for their own investment decisions",
        "B. Recognize the recommendation conflicts with the client's IPS and withdraw or modify the recommendation",
        "C. Proceed if the client provides verbal consent overriding the IPS",
        "D. Execute the trade and notify the client's compliance department afterward"
      ],
      correct: 1,
      explanation: "The dealer must ensure recommendations align with the client's documented investment parameters. A BB+ bond violates the IPS prohibition on securities rated below BBB-. The recommendation must be withdrawn or modified, regardless of the yield attractiveness."
    },
    {
      id: 15, element: 1, difficulty: "Easy",
      question: "A Mutual Fund manager is evaluated based on performance relative to the S&P/TSX Composite Index. This investment objective is best described as:",
      options: [
        "A. Liability-Driven Investing",
        "B. Absolute Return",
        "C. Relative Performance",
        "D. Capital Preservation"
      ],
      correct: 2,
      explanation: "Relative Performance means success is measured against a benchmark. If the index returns 8% and the fund returns 9%, the manager outperformed. This contrasts with Absolute Return (hedge funds targeting positive returns in any market) and LDI (pension funds matching assets to liabilities)."
    },
    {
      id: 16, element: 1, difficulty: "Hard",
      question: "An institutional client receives a large wire transfer from a third party based in a jurisdiction listed under Canadian sanctions regulations. What action must the Investment Dealer take?",
      options: [
        "A. Accept the transfer and increase monitoring frequency going forward",
        "B. Report the transaction to senior management, block the funds, and determine whether a FINTRAC report is required",
        "C. Contact the third party's financial institution for clarification before taking any action",
        "D. Process the transfer since institutional clients are subject to reduced AML scrutiny"
      ],
      correct: 1,
      explanation: "When a transfer involves a sanctioned jurisdiction, the dealer must immediately block the funds, report to senior management, and assess whether a FINTRAC report (Suspicious Transaction Report or Terrorist Property Report) is required. Institutional status does not reduce sanctions compliance obligations."
    },

    // ===== ELEMENT 2: Conflicts of Interest & Standards of Conduct (8 Qs) =====
    {
      id: 17, element: 2, difficulty: "Medium",
      question: "A Registered Representative personally owns 5,000 shares of TechCo Inc. and also recommends TechCo to several institutional clients. Under CIRO rules, what is the RR's primary obligation regarding this potential conflict?",
      options: [
        "A. Sell the personal holding before making any recommendation to clients",
        "B. Delay the recommendation until the personal holding has been held for at least 90 days",
        "C. Disclose the ownership to the Investment Dealer and obtain consent, withdrawing the recommendation if objectivity is compromised",
        "D. Continue recommending TechCo as long as the personal position is less than $100,000"
      ],
      correct: 2,
      explanation: "The RR must disclose the personal ownership to their dealer. The dealer then evaluates whether objectivity is compromised. If so, the recommendation must be withdrawn. Simply selling first (A) or waiting (B) does not address the disclosure obligation. There is no dollar threshold exemption (D)."
    },
    {
      id: 18, element: 2, difficulty: "Hard",
      question: "An Investment Dealer's corporate finance team learns about a pending acquisition, placing the target company on the firm's grey list. Despite these restrictions, an employee in corporate finance attempts to purchase shares of the target. Which statement best explains the purpose and enforcement of the grey list?",
      options: [
        "A. Grey lists are advisory only; employees may trade if they believe the information is already public",
        "B. Grey lists prevent trades based on material non-public information, blocking staff from using insider knowledge",
        "C. Grey lists apply only to the broker of record, allowing other employees to trade freely",
        "D. Grey lists restrict only short selling, not purchases of the listed securities"
      ],
      correct: 1,
      explanation: "Grey lists are a critical component of the Information Barrier (Chinese Wall). They restrict all firm employees from trading in securities where the firm possesses material non-public information (MNPI), such as a pending acquisition. The restrictions apply to all employees, not just those directly involved."
    },
    {
      id: 19, element: 2, difficulty: "Medium",
      question: "An Approved Person agrees to sit on the Board of Directors of a public company that trades through their Investment Dealer, without disclosing this arrangement to the firm. Which action is required to ensure compliance?",
      options: [
        "A. No disclosure is needed if the board position is unpaid",
        "B. The Approved Person must disclose the position to the firm and obtain prior approval to ensure conflicts are addressed",
        "C. Disclosure is only required if the Approved Person directly trades the company's securities",
        "D. Serving on external boards is automatically prohibited for all Approved Persons"
      ],
      correct: 1,
      explanation: "Approved Persons must disclose ALL outside business activities, including board positions, to their firm and obtain prior approval. This applies regardless of whether the position is paid or unpaid. The firm must evaluate potential conflicts of interest before granting approval."
    },
    {
      id: 20, element: 2, difficulty: "Hard",
      question: "A sales trader accidentally executes a buy order for 100,000 shares instead of the client's requested 10,000 shares. Panicking, the trader moves the excess 90,000 shares into a personal error account and attempts to trade out of the position over several days without reporting the error. Which standards of conduct have been violated?",
      options: [
        "A. Only the suitability determination standard, as the trade size was inappropriate",
        "B. The duty of honesty (failing to report the error), the prohibition on unauthorized trading, and potential market manipulation",
        "C. Only the confidentiality standard, as the error was not disclosed to the client",
        "D. No standards were violated since the trader was attempting to minimize client losses"
      ],
      correct: 1,
      explanation: "Multiple violations occurred: (1) Duty of honesty requires immediate reporting of errors to a Supervisor; (2) Moving shares to a personal account without authorization constitutes unauthorized trading; (3) Trading the position to 'fix' the error conceals risk from the firm. The intent to minimize losses does not excuse the violations."
    },
    {
      id: 21, element: 2, difficulty: "Medium",
      question: "An Approved Person enters into a private settlement agreement directly with an institutional client to resolve a contractual dispute, without informing their Investment Dealer. What is the regulatory view of this action?",
      options: [
        "A. The settlement is valid as long as the client agrees to the terms",
        "B. The Approved Person must disclose the terms of the settlement and it must be agreed with the Investment Dealer in advance",
        "C. Private settlements are permitted for institutional clients due to their sophistication",
        "D. The settlement is only problematic if the amount exceeds a regulatory threshold"
      ],
      correct: 1,
      explanation: "All settlements with clients must go through the firm's compliance department. 'Off-book' settlements are strictly prohibited as they hide risk from the dealer and the regulator. The Approved Person must disclose and obtain firm approval for any settlement, regardless of the client's sophistication level."
    },
    {
      id: 22, element: 2, difficulty: "Easy",
      question: "Which of the following scenarios represents the most clear violation of the \"Priority of Client Interest\" standard?",
      options: [
        "A. An RR executes a client's order during normal market hours at the prevailing market price",
        "B. An RR places a personal trade in a security immediately before executing a large client order that will move the market price (front-running)",
        "C. An RR recommends a diversified portfolio of blue-chip stocks to a conservative client",
        "D. An RR discusses general market conditions with a client during a routine portfolio review"
      ],
      correct: 1,
      explanation: "Front-running is a severe violation where the RR places personal trades before executing client orders to profit from the anticipated market movement. The client's interest must always come first — trading ahead of a known client order steals from the client."
    },
    {
      id: 23, element: 2, difficulty: "Hard",
      question: "An Investment Dealer is preparing to open an account for a new institutional client. During onboarding, a material conflict is identified: the dealer receives compensation tied to recommending certain proprietary investment products. Which action best satisfies the dealer's conflict-disclosure obligations?",
      options: [
        "A. Refer the client to a general conflicts-of-interest policy on the firm's website",
        "B. Mention conflicts verbally during the meeting and document it in internal notes",
        "C. Require the client to sign a standard industry acknowledgment form without specifics",
        "D. Disclose in writing the nature and potential impact of the conflict, and explain how the dealer will address it"
      ],
      correct: 3,
      explanation: "The dealer must provide written disclosure that specifically identifies: (1) the nature of the conflict (compensation tied to proprietary products), (2) the potential impact on the client, and (3) the method the dealer will use to address or mitigate the conflict. Generic references or verbal-only disclosures are insufficient."
    },
    {
      id: 24, element: 2, difficulty: "Medium",
      question: "A Registered Representative is offered a speaking fee and luxury travel package from an issuer whose products the RR recommends to institutional clients. Under CIRO requirements, what is the most appropriate action?",
      options: [
        "A. Decline the invitation entirely to avoid any potential conflict of interest",
        "B. Accept the fee and disclose it only in the monthly expense report to their Supervisor",
        "C. Accept if the issuer provides written confirmation of the arrangement",
        "D. Disclose the fee to clients and ask if they object before deciding"
      ],
      correct: 0,
      explanation: "Under CIRO rules, the most appropriate action is to decline the invitation to avoid any perception of a conflict of interest. Accepting benefits from issuers whose products you recommend creates a material conflict that could compromise objectivity, even if disclosed."
    },

    // ===== ELEMENT 3: Fixed Income (12 Qs) =====
    {
      id: 25, element: 3, difficulty: "Easy",
      question: "Which of the following best describes the structure of the Canadian fixed-income market?",
      options: [
        "A. It is primarily exchange-traded with real-time transparent pricing on the TSX",
        "B. It is largely an Over-the-Counter (OTC) dealer market dominated by government bonds, with corporate bonds making up a smaller portion",
        "C. It is evenly split between government and corporate bonds, all traded on centralized exchanges",
        "D. It is dominated by corporate high-yield bonds with minimal government participation"
      ],
      correct: 1,
      explanation: "The Canadian fixed-income market operates primarily as an OTC dealer market. Government bonds (federal, provincial, municipal) constitute the majority, with corporate bonds representing a smaller but significant portion. Unlike equities, there is no centralized bond exchange."
    },
    {
      id: 26, element: 3, difficulty: "Medium",
      question: "A municipal government issues bonds with two different structures: one with a bullet maturity and another with a serial maturity. Which statement correctly distinguishes these structures?",
      options: [
        "A. Bullet maturity repays principal gradually; serial maturity repays in a lump sum at the end",
        "B. Serial maturity repays principal gradually over the bond's life; bullet maturity repays principal in a single lump sum at the end",
        "C. Both structures involve equal periodic payments of principal and interest",
        "D. Bullet maturity pays no interest until the principal is repaid at maturity"
      ],
      correct: 1,
      explanation: "In a serial maturity structure, portions of the principal are repaid at predetermined intervals over the bond's life, reducing the outstanding balance over time. In a bullet maturity, the entire principal is repaid in one lump sum at the maturity date, with only interest payments made during the bond's life."
    },
    {
      id: 27, element: 3, difficulty: "Medium",
      question: "A seven-year corporate bond with a 4% annual coupon rate is currently trading at $96.50 per $100 face value. How does the bond's yield to maturity (YTM) compare to its coupon rate?",
      options: [
        "A. The YTM is higher than the 4% coupon rate",
        "B. The YTM is lower than the 4% coupon rate",
        "C. The YTM equals the 4% coupon rate",
        "D. The YTM cannot be determined without knowing the bond's credit rating"
      ],
      correct: 0,
      explanation: "When a bond trades at a discount (below par), the YTM exceeds the coupon rate. The investor receives the coupon payments PLUS a capital gain at maturity (buying at $96.50 and receiving $100). This additional return pushes the YTM above the stated coupon rate."
    },
    {
      id: 28, element: 3, difficulty: "Hard",
      question: "A bond has a face value of $1,200, an annual coupon rate of 4%, and is currently priced at $1,150. The bond matures in five years. What is the approximate yield to maturity (YTM) using the approximation formula?",
      options: [
        "A. 4.53%",
        "B. 4.94%",
        "C. 5.02%",
        "D. 6.78%"
      ],
      correct: 1,
      explanation: "Using the YTM approximation: Annual coupon = $1,200 × 4% = $48. Capital gain per year = ($1,200 - $1,150) / 5 = $10. Average price = ($1,200 + $1,150) / 2 = $1,175. Approximate YTM = ($48 + $10) / $1,175 = $58 / $1,175 ≈ 4.94%."
    },
    {
      id: 29, element: 3, difficulty: "Medium",
      question: "A bond has a modified duration of 6 years. If market interest rates increase by 1%, what is the approximate percentage change in the bond's price?",
      options: [
        "A. Increase by approximately 6%",
        "B. Decrease by approximately 6%",
        "C. Increase by approximately 1%",
        "D. Decrease by approximately 1%"
      ],
      correct: 1,
      explanation: "Modified duration measures price sensitivity to interest rate changes. The approximate price change = -Modified Duration × Change in Yield = -6 × 1% = -6%. When rates rise by 1%, the bond's price falls by approximately 6%. The negative relationship between rates and prices is fundamental."
    },
    {
      id: 30, element: 3, difficulty: "Hard",
      question: "A Canadian bond has a YTM of 4% and a modified duration of 4.5 years. If the yield increases by 0.5%, what is the approximate percentage change in the bond's price?",
      options: [
        "A. −2.25%",
        "B. +2.00%",
        "C. +2.25%",
        "D. −2.00%"
      ],
      correct: 0,
      explanation: "Price change ≈ −Modified Duration × Δ Yield = −4.5 × 0.5% = −2.25%. The bond's price decreases by approximately 2.25% when the yield increases by 0.50%. This demonstrates the inverse relationship between bond prices and yields, amplified by duration."
    },
    {
      id: 31, element: 3, difficulty: "Hard",
      question: "An institutional investor wishes to value a corporate bond with two years to maturity, a 3% annual coupon rate, and a par value of $500. The appropriate discount rate is 2.5%. What is the bond's present value?",
      options: [
        "A. $490.18",
        "B. $504.82",
        "C. $515.00",
        "D. $519.45"
      ],
      correct: 1,
      explanation: "PV = Coupon₁/(1+r) + (Coupon₂ + Par)/(1+r)². Coupon = $500 × 3% = $15. PV = $15/1.025 + ($15 + $500)/1.025² = $14.63 + $515/1.050625 = $14.63 + $490.19 = $504.82. The bond trades above par because the coupon rate (3%) exceeds the discount rate (2.5%)."
    },
    {
      id: 32, element: 3, difficulty: "Medium",
      question: "An institutional investor buys ten corporate bonds from ABC Inc. Each bond has a $1,000 par value, a 6% annual coupon, and eight years to maturity. The bonds are currently trading at $97.50 per $100 face value. If held for one full year, how much total coupon income will the investor receive?",
      options: [
        "A. $58.50",
        "B. $60.00",
        "C. $585.00",
        "D. $600.00"
      ],
      correct: 3,
      explanation: "Coupon income is based on par value, not market price. Annual coupon per bond = $1,000 × 6% = $60. Total coupon for 10 bonds = 10 × $60 = $600. The trading price ($975 per bond) affects the investor's total return but does not change the coupon payment, which is always calculated on the face value."
    },
    {
      id: 33, element: 3, difficulty: "Easy",
      question: "The Government of Canada issues a bond currently priced at par. If the Bank of Canada announces a 0.50% reduction in its key policy interest rate, what effect will this most likely have on the bond's price?",
      options: [
        "A. The bond price will rise above par",
        "B. The bond price will fall below par",
        "C. The bond price will remain at par",
        "D. The effect cannot be determined without knowing the bond's coupon rate"
      ],
      correct: 0,
      explanation: "Bond prices and interest rates move inversely. When the Bank of Canada cuts rates by 0.50%, existing bonds with higher coupon rates become more attractive relative to newly issued bonds. This increased demand pushes the price of existing bonds above par."
    },
    {
      id: 34, element: 3, difficulty: "Medium",
      question: "How do floating-rate securities typically perform compared to fixed-rate securities during periods of rising interest rates?",
      options: [
        "A. They outperform because their coupon rates adjust upward with market rates",
        "B. They underperform because their coupon rates decline as market rates rise",
        "C. Their performance is identical since both types are equally affected by rate changes",
        "D. They become illiquid as investors avoid all fixed-income products during rate hikes"
      ],
      correct: 0,
      explanation: "Floating-rate securities have coupon rates that reset periodically based on a reference rate (e.g., CORRA). When rates rise, the coupon adjusts upward, maintaining the bond's value near par. Fixed-rate bonds, by contrast, lose value as their fixed coupons become less attractive relative to higher market rates."
    },
    {
      id: 35, element: 3, difficulty: "Hard",
      question: "An institutional client primarily holding long-term fixed-rate bonds is concerned about simultaneous rising interest rates and unexpected inflation. How do these macroeconomic changes most directly threaten the client's portfolio?",
      options: [
        "A. Rising rates increase bond values while inflation enhances real coupon returns",
        "B. Rising rates depress bond prices, and inflation erodes the real purchasing power of fixed coupon payments",
        "C. Interest rate risk only affects equities, leaving fixed-income portfolios unaffected",
        "D. Inflation automatically increases coupon payments on fixed-rate bonds"
      ],
      correct: 1,
      explanation: "This is a dual threat: (1) Rising rates cause bond prices to fall (price risk); (2) Inflation reduces the real value of the fixed coupon payments (purchasing power risk). Together, the client faces both capital losses and declining real income — the worst scenario for a long-term fixed-rate bond portfolio."
    },
    {
      id: 36, element: 3, difficulty: "Medium",
      question: "\"EnergyCorp\" is rated BBB (low) by DBRS. Following a severe downturn, DBRS downgrades the company to BB (high). A pension fund holding $50 million of EnergyCorp bonds has an Investment Policy Statement prohibiting high-yield holdings. What is the most likely consequence?",
      options: [
        "A. The pension fund can continue holding since BB is close to investment grade",
        "B. The pension fund faces forced selling of the bonds, potentially at fire-sale prices due to a liquidity crisis",
        "C. The downgrade automatically converts the bonds to equity securities",
        "D. The pension fund's IPS is overridden by the fund manager's discretion during market stress"
      ],
      correct: 1,
      explanation: "When a bond crosses from BBB- to BB+ (the 'Fallen Angel' threshold), institutions with IPS restrictions against high-yield debt are legally forced to sell. Since many institutions have the same restriction, simultaneous selling creates a liquidity crisis in the OTC market, pushing prices down dramatically."
    },

    // ===== ELEMENT 4: Equities (13 Qs) =====
    {
      id: 37, element: 4, difficulty: "Easy",
      question: "Which type of business structure is typically most flexible in terms of profit distribution and decision-making?",
      options: [
        "A. Corporation",
        "B. Co-operative",
        "C. Non-governmental organization",
        "D. Partnership"
      ],
      correct: 3,
      explanation: "Partnerships offer the most flexibility: partners can agree to any profit-sharing ratio through the partnership agreement, and decisions can be made quickly without the formal governance structures required by corporations (Board of Directors, shareholder meetings)."
    },
    {
      id: 38, element: 4, difficulty: "Medium",
      question: "A corporation has issued both common and preferred shares. Which statement most accurately describes the dividend rights of common shareholders?",
      options: [
        "A. Common shareholders receive dividends only if the corporation exceeds a revenue threshold",
        "B. Common shareholders receive dividends only after preferred shareholders have been paid their entitled dividends",
        "C. Common shareholders receive a fixed annual dividend regardless of financial performance",
        "D. Common shareholders can receive dividends at any time, even if preferred dividends have been skipped"
      ],
      correct: 1,
      explanation: "Preferred shareholders have priority in dividend payments. Common shareholders receive dividends only after the preferred dividend obligation has been met. This is a fundamental characteristic of the capital structure hierarchy."
    },
    {
      id: 39, element: 4, difficulty: "Medium",
      question: "A manufacturing firm enacts a two-for-one reverse stock split to keep its share price above minimum listing requirements. How does this affect shareholders?",
      options: [
        "A. Each shareholder's total economic value is permanently reduced by 50%",
        "B. The number of shares outstanding is reduced, but the price per share increases proportionally, leaving total shareholder value unchanged",
        "C. Shareholders receive higher dividend payments on the reduced share count",
        "D. Shareholders automatically forfeit half their shares to the company at no cost"
      ],
      correct: 1,
      explanation: "A reverse split consolidates shares (e.g., 2 shares become 1) while proportionally increasing the price per share. If you held 1,000 shares at $2 each ($2,000 total), after a 2:1 reverse split you hold 500 shares at $4 each ($2,000 total). Total value is unchanged."
    },
    {
      id: 40, element: 4, difficulty: "Hard",
      question: "A specialized manufacturer issues two classes of preferred shares: Class X (participating dividends if profits exceed targets) and Class Y (fixed dividends, no surplus participation). Which statement best captures the trade-offs for institutional investors?",
      options: [
        "A. Class X holders receive no extra dividends even when profits rise, making Class Y more attractive",
        "B. Both classes produce identical returns regardless of firm profitability",
        "C. Class Y's steady dividend always exceeds any Class X surplus payout when earnings spike",
        "D. Class X offers potential upside through surplus-based dividends, while Class Y provides predictable fixed income with no upside participation"
      ],
      correct: 3,
      explanation: "Participating preferred shares (Class X) allow holders to share in profits above the base dividend when certain thresholds are met, providing upside potential. Non-participating preferred shares (Class Y) pay only the fixed dividend regardless of profitability, offering predictability but no additional upside."
    },
    {
      id: 41, element: 4, difficulty: "Hard",
      question: "A beverage company just paid a $2.00 annual dividend. Dividends are expected to grow at 4% annually, and the investor requires a 9% return. Using the Gordon Growth Model, what is the fair value of the stock?",
      options: [
        "A. $40.00",
        "B. $41.60",
        "C. $43.60",
        "D. $44.00"
      ],
      correct: 1,
      explanation: "Gordon Growth Model: P₀ = D₁ / (r - g) where D₁ = D₀ × (1 + g). D₁ = $2.00 × 1.04 = $2.08. P₀ = $2.08 / (0.09 - 0.04) = $2.08 / 0.05 = $41.60. The key is to use the NEXT year's expected dividend (D₁), not the current dividend (D₀)."
    },
    {
      id: 42, element: 4, difficulty: "Medium",
      question: "An investor is evaluating a company's stock. The company reports EPS of $3.50 and the stock trades at $42.00. The industry average P/E ratio is 10. Based on the P/E analysis, which conclusion is most appropriate?",
      options: [
        "A. The stock is undervalued because its P/E ratio of 12 is below the industry average",
        "B. The stock is overvalued because its P/E ratio of 12 exceeds the industry average of 10",
        "C. The stock is undervalued because its P/E ratio of 9 is below the industry average",
        "D. The stock is fairly valued because its price matches its earnings perfectly"
      ],
      correct: 1,
      explanation: "P/E = Price / EPS = $42.00 / $3.50 = 12.0. The stock's P/E of 12 exceeds the industry average of 10, suggesting investors are paying more per dollar of earnings than the sector norm. This indicates the stock may be overvalued relative to its peers."
    },
    {
      id: 43, element: 4, difficulty: "Easy",
      question: "What does the transferability feature of common shares refer to?",
      options: [
        "A. The ability of shareholders to transfer ownership without restrictions on a public exchange",
        "B. The restriction on transferring shares unless the company gives specific approval",
        "C. The ability of shareholders to transfer only voting rights, not economic interests",
        "D. The right to transfer dividend payments to another party while retaining share ownership"
      ],
      correct: 0,
      explanation: "For publicly listed common shares, transferability means shareholders can freely buy and sell their shares on the exchange without requiring company approval. This is a key feature of public corporations that provides liquidity to investors."
    },
    {
      id: 44, element: 4, difficulty: "Medium",
      question: "Which statement best describes how Canadian Depositary Receipts (CDRs) trade in Canada and the rights they confer?",
      options: [
        "A. CDRs are locked until maturity and provide no voting or dividend rights",
        "B. CDRs trade freely on Canadian exchanges but lack any voting or dividend entitlements",
        "C. CDRs are listed for open trading, and both voting and dividend rights reflect fractional ownership of the underlying foreign shares",
        "D. CDRs provide a fixed dividend schedule but cannot be resold after purchase"
      ],
      correct: 2,
      explanation: "CDRs trade on Canadian exchanges (like the NEO Exchange) and represent fractional ownership in foreign company shares. Holders receive proportionate voting rights and dividends based on their fractional ownership, with currency hedging built into the CDR structure."
    },
    {
      id: 45, element: 4, difficulty: "Hard",
      question: "An institutional investor compares buying CDRs on a high-growth foreign e-commerce stock (which includes hedging fees) versus domestic Class B shares with moderate dividends. The investor seeks maximum long-term net gains despite exchange rate uncertainty. Which approach best fits this objective?",
      options: [
        "A. Always select domestic shares, as all currency exposure inevitably leads to net losses",
        "B. Use leverage on domestic shares to amplify returns while avoiding any foreign exposure",
        "C. Avoid both alternatives since dividend-paying shares always underperform currency-hedged instruments",
        "D. Choose the CDR if the foreign stock's higher growth potential offsets currency movements and hedging fees, maximizing net returns overall"
      ],
      correct: 3,
      explanation: "The analysis should focus on total net returns. If the foreign company's growth potential significantly exceeds the domestic alternative even after accounting for currency risk and hedging costs, the CDR is the better choice. The decision requires comparing expected net returns, not making blanket assumptions about currency exposure."
    },
    {
      id: 46, element: 4, difficulty: "Medium",
      question: "Under National Instrument 41-101 (General Prospectus Requirements), what is the primary purpose of a prospectus in a public offering?",
      options: [
        "A. To provide marketing material that promotes the issuer's investment potential",
        "B. To disclose comprehensive information about the issuer to enable investors to make informed decisions",
        "C. To serve as the Investment Dealer's due diligence report on the issuing company",
        "D. To ensure the securities commission has up-to-date records of all public companies"
      ],
      correct: 1,
      explanation: "A prospectus is a legal disclosure document required under NI 41-101. Its primary purpose is to provide full, true, and plain disclosure of all material facts about the issuer and the securities being offered, enabling investors to make informed investment decisions."
    },
    {
      id: 47, element: 4, difficulty: "Medium",
      question: "An institutional investor debates between purchasing common shares for capital growth and preferred shares for stable income. Which consideration best captures the fundamental trade-off?",
      options: [
        "A. Preferred shares appreciate faster than common shares when interest rates fall",
        "B. Preferred shares offer steadier dividends but limited growth potential, while common shares provide stronger capital appreciation potential",
        "C. Common shares always pay higher income than preferred shares",
        "D. Both share classes carry identical long-term risk profiles and dividend policies"
      ],
      correct: 1,
      explanation: "This is the core equity trade-off: preferred shares provide predictable dividend income with priority in payment but limited upside. Common shares offer unlimited growth potential through capital appreciation but carry more risk and have no guaranteed dividend."
    },
    {
      id: 48, element: 4, difficulty: "Hard",
      question: "A financial analyst is reviewing a high-growth technology firm that has not yet achieved profitability (negative earnings). Which stock valuation approach is most appropriate for this company?",
      options: [
        "A. Price-to-Earnings (P/E) ratio",
        "B. Price-to-Sales (P/S) ratio",
        "C. Dividend Discount Model",
        "D. Net Asset Value (NAV)"
      ],
      correct: 1,
      explanation: "For unprofitable companies, P/E ratio is meaningless (negative earnings produce negative P/E). The Dividend Discount Model requires dividends, which unprofitable firms typically don't pay. Price-to-Sales ratio works because revenue exists even when earnings don't, making it the most appropriate metric for high-growth, pre-profit companies."
    },
    {
      id: 49, element: 4, difficulty: "Medium",
      question: "An institutional investor is comparing two companies in the same sector using P/E ratios. Company A has a P/E of 15, while Company B has a P/E of 25. Assuming similar risk profiles, which conclusion is most reasonable?",
      options: [
        "A. Company A is overvalued relative to Company B",
        "B. Company B's stock is more liquid than Company A's",
        "C. Company A is more expensive per dollar of earnings than Company B",
        "D. The market expects Company B to achieve higher future earnings growth than Company A"
      ],
      correct: 3,
      explanation: "A higher P/E ratio indicates investors are willing to pay more per dollar of current earnings, typically because they expect higher future earnings growth. Company B's P/E of 25 suggests the market anticipates stronger growth prospects compared to Company A's P/E of 15."
    },

    // ===== ELEMENT 5: Securities Analysis & Investment Theory (31 Qs) =====
    {
      id: 50, element: 5, difficulty: "Medium",
      question: "A company has $1.5 million in cash, $800,000 in equipment, and $300,000 in short-term investments. It also has $500,000 in long-term debt and $200,000 in accounts payable. How should these items be classified on the statement of financial position?",
      options: [
        "A. Cash, short-term investments, and accounts payable are current items; equipment and long-term debt are non-current",
        "B. All items are classified as current since they affect the company's operations",
        "C. Cash and equipment are current assets; all liabilities are non-current",
        "D. Short-term investments and long-term debt are both classified as non-current items"
      ],
      correct: 0,
      explanation: "Current items are expected to be converted to cash or settled within one year: cash ($1.5M), short-term investments ($300K), and accounts payable ($200K). Non-current items extend beyond one year: equipment ($800K) and long-term debt ($500K)."
    },
    {
      id: 51, element: 5, difficulty: "Hard",
      question: "A company reports strong net income but a significant decrease in cash from operating activities. Which explanation is most likely for this discrepancy?",
      options: [
        "A. The company made significant repayments on long-term debt",
        "B. The company had high levels of non-cash expenses such as depreciation",
        "C. The company recorded a large portion of sales on credit, increasing accounts receivable substantially",
        "D. The company issued new shares to raise capital"
      ],
      correct: 2,
      explanation: "Credit sales increase revenue (boosting net income) without generating immediate cash. The receivables grow but cash doesn't arrive until customers pay. Debt repayment (A) and share issuance (D) are financing activities, not operating. High depreciation (B) would actually help operating cash flow since it's a non-cash expense added back."
    },
    {
      id: 52, element: 5, difficulty: "Hard",
      question: "Company Y reports: Current assets $450,000 (cash $80,000, receivables $150,000, inventory $220,000); Current liabilities $300,000. What can be concluded about the company's liquidity?",
      options: [
        "A. Both current ratio and quick ratio exceed 1, indicating strong overall liquidity",
        "B. The quick ratio exceeds 1, confirming the company can cover liabilities with liquid assets",
        "C. The current ratio is above 1 indicating adequate total coverage, but the quick ratio is below 1, suggesting potential difficulty meeting obligations quickly",
        "D. Both ratios indicate weak liquidity across all measures"
      ],
      correct: 2,
      explanation: "Current ratio = $450,000 / $300,000 = 1.50 (above 1, adequate). Quick ratio = (Cash + Receivables) / Current Liabilities = ($80,000 + $150,000) / $300,000 = $230,000 / $300,000 = 0.77 (below 1). The company can cover total current obligations but may struggle with immediate liquid obligations since inventory ($220K) is not quickly convertible."
    },
    {
      id: 53, element: 5, difficulty: "Medium",
      question: "An equity research analyst discovers strong financial results in a company's annual report but cautionary language about regulatory risks in the earnings call transcript, and independent analyst reports suggesting overstated revenue growth. How should the analyst proceed?",
      options: [
        "A. Rely solely on the quantitative data from annual reports, assuming past performance predicts the future",
        "B. Base the decision primarily on the cautionary statements, disregarding the financial numbers",
        "C. Integrate quantitative financial data with qualitative disclosures and evaluate independent analyst reports for a comprehensive view",
        "D. Focus exclusively on independent analyst reports as the most unbiased source"
      ],
      correct: 2,
      explanation: "Comprehensive analysis requires integrating all available information: quantitative data (financial statements), qualitative disclosures (earnings call commentary), and third-party perspectives (independent reports). No single source should be relied upon exclusively — the Mosaic Theory approach builds a complete picture from multiple information tiles."
    },
    {
      id: 54, element: 5, difficulty: "Medium",
      question: "Which of the following is a type of information provided directly by equity issuers (primary sources)?",
      options: [
        "A. Annual financial statements filed on SEDAR+",
        "B. Real-time stock price quotes from the exchange",
        "C. Trading volume data from market data providers",
        "D. Regulatory enforcement filings from securities commissions"
      ],
      correct: 0,
      explanation: "Annual financial statements are primary sources produced directly by the issuer. Stock prices (B), trading volumes (C), and regulatory filings (D) come from exchanges, data providers, or regulators — these are secondary sources. The distinction between primary and secondary information sources is critical for due diligence."
    },
    {
      id: 55, element: 5, difficulty: "Medium",
      question: "An economist uses the S&P/TSX Composite Index and the Nikkei 225 to advise on international diversification. How do these country-specific indices best assist the analysis?",
      options: [
        "A. They rank individual companies by profitability within each country",
        "B. They measure each country's equity market performance to assess potential returns relative to global trends",
        "C. They calculate aggregate dividend yields for each country's stock market",
        "D. They assess trading liquidity in each country's equity markets to guide entry timing"
      ],
      correct: 1,
      explanation: "Country-specific equity indices serve as benchmarks for overall market performance. By comparing the S&P/TSX and Nikkei 225, the economist can evaluate each market's return characteristics, correlations, and risk profiles to formulate diversification recommendations across geographies."
    },
    {
      id: 56, element: 5, difficulty: "Hard",
      question: "A global retailer reports $300 million net income plus a $50 million other comprehensive loss from a foreign currency translation adjustment. The company then sells the foreign subsidiary, reclassifying the $50 million loss. How does this reclassification affect reported figures?",
      options: [
        "A. Net income remains $300M and total comprehensive income stays at $250M — reclassification has no further effect",
        "B. Neither net income nor total comprehensive income changes from the reclassification",
        "C. Reclassification can never decrease net income, so total comprehensive income must exceed $250M",
        "D. Net income drops to $250M when the $50M loss is reclassified through the income statement, and total comprehensive income remains at $250M"
      ],
      correct: 3,
      explanation: "Upon disposal of the subsidiary, the accumulated $50M OCI loss is 'recycled' (reclassified) from OCI into net income. Net income drops from $300M to $250M. However, total comprehensive income (net income + OCI) remains $250M because the loss was already captured in comprehensive income — it merely shifts categories."
    },
    {
      id: 57, element: 5, difficulty: "Hard",
      question: "A tech manufacturer sells part of its underperforming division for $2 million, booking a disposal gain in net income. However, operating cash flow remains negative. What best explains this discrepancy?",
      options: [
        "A. The disposal proceeds appear under operating activities, offsetting the negative cash flow",
        "B. The cash from the division sale is classified as an investing activity, so the operating cash flow deficit persists",
        "C. Reporting a disposal gain in income means no separate cash flow entry is needed",
        "D. Including disposal profits automatically corrects negative operating cash flow"
      ],
      correct: 1,
      explanation: "Proceeds from selling a business division are classified as investing activities on the cash flow statement. The gain increases net income but does not improve operating cash flow. This demonstrates why analysts must examine cash flow statements separately — net income can be inflated by one-time investing gains while core operations remain cash-negative."
    },
    {
      id: 58, element: 5, difficulty: "Medium",
      question: "A software firm receives a qualified audit opinion due to scope limitations on valuing proprietary intangible assets. Footnotes mention an IP dispute that could affect future revenue. What is the significance for investors?",
      options: [
        "A. A qualified opinion is equivalent to an unmodified opinion, so the IP dispute is immaterial",
        "B. Footnotes are optional and don't affect the auditor's conclusions",
        "C. An IP dispute mentioned in footnotes confirms imminent bankruptcy",
        "D. The qualified opinion signals unresolved valuation risks, and the footnotes highlight legal uncertainties requiring closer investor scrutiny"
      ],
      correct: 3,
      explanation: "A qualified opinion indicates the auditor found specific issues that couldn't be resolved but aren't pervasive enough to warrant an adverse opinion. Combined with footnotes about IP disputes, investors should exercise heightened scrutiny — the financial statements may not fully reflect all risks."
    },
    {
      id: 59, element: 5, difficulty: "Hard",
      question: "An energy producer trades at a P/E ratio of 30, exceeding its sector average of 20. The company has cyclical earnings that vary widely and recently raised its dividend. Which factor best helps an analyst evaluate whether the high P/E is justified?",
      options: [
        "A. Comparing the P/E to other sectors provides better insight than same-sector comparisons",
        "B. Using normalized earnings and a dividend discount model assessment to determine if sustained growth supports the elevated P/E",
        "C. A direct P/E comparison to industry peers immediately confirms overvaluation",
        "D. The dividend increase alone guarantees earnings stability and justifies the premium"
      ],
      correct: 1,
      explanation: "For cyclical companies, current P/E ratios can be misleading because earnings fluctuate. Normalizing earnings (averaging across the cycle) provides a more accurate P/E. Combined with a DDM assessment of dividend sustainability, the analyst can determine whether the market's growth expectations are reasonable."
    },
    {
      id: 60, element: 5, difficulty: "Medium",
      question: "A pension fund applies mean-variance portfolio theory to reduce total volatility while seeking growth. Which approach best achieves efficient diversification?",
      options: [
        "A. Concentrate capital in one high-yield bond issuer with strong historical returns",
        "B. Use correlations across multiple asset classes to combine securities that don't move in lockstep",
        "C. Allocate equal amounts to each security without analyzing correlation or variance",
        "D. Invest entirely in a broad equity index without any fixed-income allocation"
      ],
      correct: 1,
      explanation: "Mean-variance theory's core insight is that combining assets with low correlations reduces portfolio risk without proportionally reducing returns. By analyzing the covariance matrix, the fund can construct an efficient portfolio on the efficient frontier — achieving maximum return for a given level of risk."
    },
    {
      id: 61, element: 5, difficulty: "Medium",
      question: "An equity analyst applies the Capital Asset Pricing Model (CAPM) and then a multi-factor model (adding size and value factors) to a mid-cap resources company. The models produce different required returns. Why?",
      options: [
        "A. CAPM already captures all firm-specific factors, making multi-factor models redundant",
        "B. Multi-factor models incorporate additional risk dimensions beyond market beta, shifting the required return calculation",
        "C. Adding factors always reduces required returns regardless of company characteristics",
        "D. Multi-factor models assume zero correlation with the market, making beta obsolete"
      ],
      correct: 1,
      explanation: "CAPM uses only market risk (beta) to determine required return. Multi-factor models (like Fama-French) add size premium and value premium factors. A mid-cap (smaller) resources company would likely have higher required returns under the multi-factor model because it captures additional risk dimensions that CAPM ignores."
    },
    {
      id: 62, element: 5, difficulty: "Medium",
      question: "A value-focused fund manager shifts strategy to chase high-momentum growth stocks, scaling back undervalued positions. Which statement best describes the potential impact of this style drift?",
      options: [
        "A. Adopting momentum stocks guarantees higher returns and removes the need for established processes",
        "B. Growth and value investing are identical, so the shift merely refines the existing approach",
        "C. The style change has minimal effect on portfolio risk or client expectations",
        "D. Style drift may capture short-term gains but jeopardizes the core investment thesis, confuses value-oriented clients, and increases portfolio risk"
      ],
      correct: 3,
      explanation: "Style drift — deviating from the stated investment mandate — is a serious governance concern. Clients invested in a value strategy expect value discipline. Switching to momentum/growth changes the risk profile, potentially violating the IPS, and undermines client trust and fund credibility."
    },
    {
      id: 63, element: 5, difficulty: "Medium",
      question: "An institutional investor holds a broad-market ETF for diversification but notices a sector-specific index fund outperforming. The investor considers shifting capital while remaining passive overall. What is the most balanced approach?",
      options: [
        "A. Switch entirely to the sector fund to capture recent outperformance",
        "B. Sell part of the broad ETF to allocate to the sector fund, balancing increased returns against the risk of narrower exposure",
        "C. Abandon passive investing since sector outperformance proves active management is superior",
        "D. Ignore sector variations entirely since they are irrelevant to long-term returns"
      ],
      correct: 1,
      explanation: "A partial reallocation balances the opportunity to capture sector outperformance with the risk management benefits of broad diversification. This 'core-satellite' approach maintains the passive core while adding tactical satellite positions — a common institutional strategy."
    },
    {
      id: 64, element: 5, difficulty: "Medium",
      question: "A passive equity fund tracking a Canadian index sees a major energy stock removed and a tech firm added to the index. How should the fund manager respond to minimize tracking error?",
      options: [
        "A. Immediately align holdings with the updated index constituents",
        "B. Wait for the next scheduled rebalancing date regardless of index changes",
        "C. Use leverage to offset the removed energy weighting without buying the new tech stock",
        "D. Liquidate all positions and rebuild after the new index fully settles"
      ],
      correct: 0,
      explanation: "For a passive index-tracking fund, minimizing tracking error requires promptly adjusting holdings to mirror index changes. Waiting (B) increases tracking error. Leverage (C) adds unnecessary risk. Full liquidation (D) incurs excessive transaction costs and temporary market exposure gaps."
    },
    {
      id: 65, element: 5, difficulty: "Hard",
      question: "A pension fund manages a passive buy-and-hold corporate bond portfolio but observes yield-spread shifts across sectors, creating potential mispricing opportunities. Which approach best addresses these changes while preserving mostly passive principles?",
      options: [
        "A. Hold all bonds strictly to maturity, ignoring all spread changes",
        "B. Periodically rebalance into cheaper sectors to exploit mispriced spreads while maintaining the overall passive bond structure",
        "C. Short-sell government bonds whenever corporate spreads tighten",
        "D. Abandon passive rules entirely and adopt a fully active trading approach"
      ],
      correct: 1,
      explanation: "Enhanced indexing or 'passive plus' allows periodic rebalancing to exploit obvious spread mispricings while maintaining the overall passive framework. This captures incremental return without the costs and risks of full active management, preserving the fund's passive mandate."
    },
    {
      id: 66, element: 5, difficulty: "Medium",
      question: "An equity manager follows a buy-and-hold strategy in large-cap stocks but is concerned that market price movements may cause unintended sector concentration. Which passive technique best manages this long-term imbalance?",
      options: [
        "A. Apply quarterly market timing to actively adjust sector weights",
        "B. Use derivative hedges that mimic active sector rotations",
        "C. Periodically rebalance holdings back to target weights or track an index, preserving low turnover",
        "D. Allow sector imbalances to grow unchecked, trusting momentum to outperform"
      ],
      correct: 2,
      explanation: "Periodic rebalancing is the cornerstone passive technique for managing sector drift. By systematically selling winners and buying laggards to restore target weights, the fund maintains its intended risk profile with minimal turnover and transaction costs."
    },
    {
      id: 67, element: 5, difficulty: "Medium",
      question: "A real-estate developer's offering memorandum warns of \"no guaranteed liquidity,\" yet promotional brochures promise \"minimal risk and quick exit.\" What should an institutional investor do to reconcile these conflicting claims?",
      options: [
        "A. Contact the developer directly for an explanation of the wording",
        "B. File a complaint with the securities regulator immediately",
        "C. Verify discrepancies between the official memorandum and promotional claims through their Investment Dealer",
        "D. Rely solely on the offering memorandum and ignore marketing materials"
      ],
      correct: 2,
      explanation: "The investor should work through their Investment Dealer to verify discrepancies between the legally binding offering memorandum and the marketing claims. The dealer has a duty to perform product due diligence and ensure promotional materials comply with Canadian securities disclosure requirements."
    },
    {
      id: 68, element: 5, difficulty: "Hard",
      question: "A fixed-income fund manager wants to add high-yield corporate bonds to an existing investment-grade portfolio. Which approach best balances the increased credit risk with return objectives?",
      options: [
        "A. Concentrate the entire portfolio into one high-yield issuer to maximize yield",
        "B. Short equity futures as a credit hedge for the fixed-income allocation",
        "C. Diversify across multiple high-yield issuers while maintaining a core of investment-grade bonds to stabilize overall credit quality",
        "D. Sell all lower-rated bonds after early price gains to eliminate any credit risk"
      ],
      correct: 2,
      explanation: "The barbell approach — combining high-yield bonds (for return enhancement) with investment-grade bonds (for stability) — is a proven strategy. Diversification across multiple high-yield issuers reduces idiosyncratic default risk while the investment-grade core provides a quality anchor."
    },
    {
      id: 69, element: 5, difficulty: "Easy",
      question: "According to loanable funds theory, if savings increase the supply of loanable funds by 20% but government borrowing increases demand by only 10%, what is the most likely short-term effect on interest rates?",
      options: [
        "A. Interest rates will increase",
        "B. Interest rates will decrease",
        "C. Interest rates will remain unchanged",
        "D. Interest rates will fluctuate unpredictably"
      ],
      correct: 1,
      explanation: "In loanable funds theory, interest rates are determined by supply and demand for funds. If supply increases (20%) more than demand (10%), there is a net surplus of loanable funds. This excess supply pushes interest rates downward as lenders compete to place their money."
    },
    {
      id: 70, element: 5, difficulty: "Hard",
      question: "A Canadian manufacturing company relies heavily on debt financing for expansion. The Bank of Canada signals upcoming interest rate hikes. What strategic action would be most appropriate for the company?",
      options: [
        "A. Delay all borrowing and expansion until rates stabilize",
        "B. Accelerate borrowing at current rates before the hikes take effect to lock in lower costs",
        "C. Continue borrowing at the same pace since rate changes have no material impact",
        "D. Reduce the workforce to offset anticipated higher borrowing costs"
      ],
      correct: 0,
      explanation: "When rate hikes are signaled, the prudent strategy is to delay discretionary borrowing until there is more clarity on the rate path and economic conditions. Accelerating borrowing (B) assumes rates will definitely rise substantially — not guaranteed. Workforce reductions (D) are an extreme measure not warranted by rate signals alone."
    },
    {
      id: 71, element: 5, difficulty: "Easy",
      question: "Which risk metric best measures how much a stock's value has declined from its highest point during a market downturn?",
      options: [
        "A. Beta",
        "B. Maximum drawdown",
        "C. Standard deviation",
        "D. Sharpe ratio"
      ],
      correct: 1,
      explanation: "Maximum drawdown measures the largest peak-to-trough decline in an investment's value. It directly answers 'what was the worst loss from peak?' Beta measures market sensitivity, standard deviation measures overall volatility, and the Sharpe ratio measures risk-adjusted returns."
    },
    {
      id: 72, element: 5, difficulty: "Medium",
      question: "An Investment Dealer discovers an employee embezzling funds disguised through client transactions. The fraud was uncovered during a review of mandatory two-week vacation compliance. What type of risk does this represent?",
      options: [
        "A. Insider trading risk",
        "B. Operational risk",
        "C. Bribery risk",
        "D. Money laundering risk"
      ],
      correct: 1,
      explanation: "This is operational risk — the risk of loss from inadequate or failed internal processes, people, or systems. Employee fraud is a classic operational risk event. Mandatory vacation policies exist specifically as a control to uncover such fraud, since fraudulent activities typically require the perpetrator's continuous presence."
    },
    {
      id: 73, element: 5, difficulty: "Medium",
      question: "A mining company launches a takeover bid for a smaller competitor, issuing a bid circular under National Instrument 62-104. Which requirement ensures minority shareholders are protected?",
      options: [
        "A. No official documentation is required if the bidder expects quick acceptance",
        "B. Extended review periods apply only to major shareholders, not minority investors",
        "C. A formal bid circular plus a minimum tender period allows all shareholders to evaluate the offer details",
        "D. The bid may close at any time, bypassing formal documentation requirements"
      ],
      correct: 2,
      explanation: "NI 62-104 requires the bidder to deliver a formal bid circular containing all material information and maintain a minimum tender period (currently 105 days with possible shortening). This ensures ALL shareholders — including minorities — have adequate time and information to make an informed decision."
    },
    {
      id: 74, element: 5, difficulty: "Medium",
      question: "A mining corporation announces its annual shareholders meeting, issuing proxy materials on SEDAR+. Which statutory right ensures minority shareholders can participate?",
      options: [
        "A. Only majority shareholders can cast votes; minority shareholders have no meeting participation rights",
        "B. Every shareholder, regardless of the size of their holding, receives meeting notices and has full voting privileges",
        "C. Meeting details are confidential until after the event to prevent trading on the information",
        "D. Proxy materials exclude forward-looking information to prevent biased voting decisions"
      ],
      correct: 1,
      explanation: "Corporate law provides that every shareholder — regardless of holding size — has the statutory right to receive notice of meetings, attend, ask questions of management, and vote on proposed resolutions. This fundamental right ensures democratic corporate governance."
    },
    {
      id: 75, element: 5, difficulty: "Hard",
      question: "A global crisis triggers commodity price swings, leading Canadian regulators to revise margin requirements for commodity futures. What must an Investment Dealer address to comply while managing increased volatility?",
      options: [
        "A. Avoid monitoring commodity positions since intraday volatility is temporary",
        "B. Reassess margin policies, secure additional collateral from clients, and refine hedging strategies to meet updated regulatory requirements",
        "C. Encourage clients to ignore margin calls during volatility, trusting markets to recover",
        "D. Seek exemptions from margin requirements since strict rules are impractical during crises"
      ],
      correct: 1,
      explanation: "Dealers must proactively adjust to new margin rules by: (1) updating internal margin policies, (2) collecting additional collateral where required, and (3) enhancing hedging and risk management. Ignoring margin calls or seeking exemptions would violate regulatory obligations and expose the firm to catastrophic losses."
    },
    {
      id: 76, element: 5, difficulty: "Hard",
      question: "A venture fund invests in an early-stage tech startup and considers digital tokens issued by the same startup. Which distinction between private equity shares and crypto-assets is most critical for the investor?",
      options: [
        "A. Private equity shares are publicly listed; digital tokens guarantee fixed redemptions",
        "B. Crypto-assets require formal due diligence while private equity is largely unregulated",
        "C. Both asset types ensure guaranteed exit strategies within six months",
        "D. Crypto-assets may be highly volatile and subject to evolving regulations, while private equity typically involves long lock-up periods and uncertain liquidity"
      ],
      correct: 3,
      explanation: "The key distinction is the risk/liquidity profile: crypto-assets can experience extreme price volatility with evolving (sometimes unclear) regulatory frameworks. Private equity involves long-term capital commitments (5-10 year lock-ups) with illiquid holdings. Neither offers easy or guaranteed exit strategies."
    },
    {
      id: 77, element: 5, difficulty: "Medium",
      question: "An institution designs a structured product offering downside capital protection with a capped equity index return. Which trade-off should investors consider most carefully compared to direct index investment?",
      options: [
        "A. Structured products perfectly replicate index returns with no limitations",
        "B. Downside protection comes at the cost of capped upside; investors must evaluate fees and issuer credit risk",
        "C. Direct index investment provides identical capital protection to structured products",
        "D. Structured product losses are fully insured by regulators during market crashes"
      ],
      correct: 1,
      explanation: "Structured products protect downside by sacrificing upside potential through caps. Investors must evaluate: (1) the cost of the protection (embedded fees), (2) the upside cap limiting gains, and (3) issuer credit risk — if the issuer defaults, the protection is worthless. Regulators do not insure these products."
    },
    {
      id: 78, element: 5, difficulty: "Medium",
      question: "A pension fund considers investing in a toll highway for inflation protection and stable yield. Which statement best describes the trade-offs?",
      options: [
        "A. Toll highways can hedge inflation through escalating tolls but are illiquid, require ongoing maintenance, and face regulatory constraints",
        "B. Toll highway investments have negligible costs and complete liquidity",
        "C. Toll highways are exempt from local regulations, eliminating political risk",
        "D. High toll fees are irrelevant if road management is outsourced"
      ],
      correct: 0,
      explanation: "Infrastructure investments like toll highways offer inflation linkage (tolls often increase with CPI) and steady cash flows. However, they are illiquid (can't sell quickly), require ongoing capital expenditure for maintenance, and face regulatory/political risks (government may cap toll increases)."
    },
    {
      id: 79, element: 5, difficulty: "Hard",
      question: "An institutional investor holds depository receipts (DRs) representing shares of a foreign company. The underlying share price rises 8%, but the DR price increases only 5%. What most likely explains this 3% gap?",
      options: [
        "A. The custodian bank increased DR management fees by 3%",
        "B. The foreign government imposed a 3% dividend withholding tax",
        "C. The foreign currency depreciated by approximately 3% against the Canadian dollar",
        "D. DR trading liquidity decreased by approximately 3%"
      ],
      correct: 2,
      explanation: "When the foreign currency weakens against the Canadian dollar, foreign share gains are partially offset by unfavorable currency conversion. An 8% local currency gain combined with approximately 3% currency depreciation produces approximately 5% in Canadian dollar terms. This is currency (exchange rate) risk in action."
    },
    {
      id: 80, element: 5, difficulty: "Medium",
      question: "A Registered Representative is reviewing an institutional client's account containing securities transferred in from another firm and client-directed trades. Why is it important to understand these holdings?",
      options: [
        "A. To determine if the investments are performing well and whether trades should be recommended",
        "B. To ensure the RR understands the potential obligations and risks these securities may impose on the client",
        "C. To assess opportunities for new product cross-selling",
        "D. To ensure a balanced mix of low-risk and high-risk securities at all times"
      ],
      correct: 1,
      explanation: "Understanding transferred-in and client-directed securities is essential because they may carry specific risks or obligations (e.g., derivative positions with margin calls, illiquid private placements with lock-up provisions) that could materially affect the client's overall portfolio risk."
    },

    // ===== ELEMENT 6: Managed & Other Products (8 Qs) =====
    {
      id: 81, element: 6, difficulty: "Medium",
      question: "An institutional investor is comparing purchasing REIT shares directly versus investing in a REIT ETF. Which statement correctly describes the key difference?",
      options: [
        "A. REIT shares never trade publicly, making REIT ETFs permanently superior for liquidity",
        "B. A REIT ETF invests directly into property, while a REIT invests into property indices",
        "C. Some REITs are non-exchange-traded with limited liquidity, while REIT ETFs are always exchange-traded and provide intraday liquidity",
        "D. REIT ETFs are far less liquid than individual REIT shares"
      ],
      correct: 2,
      explanation: "The key difference is in trading mechanics: while some REITs are publicly listed, others are non-traded (private) with limited liquidity. REIT ETFs, by definition, always trade on exchanges, providing continuous intraday liquidity and transparent pricing regardless of the underlying REITs' trading status."
    },
    {
      id: 82, element: 6, difficulty: "Medium",
      question: "An institutional investor considers purchasing an ETF with a NAV of $50.00 per share that is currently trading at $50.20 per share. What does this price relationship imply?",
      options: [
        "A. The ETF is trading at a discount, presenting a buying opportunity",
        "B. The ETF is trading at a premium, meaning investors pay more than the value of the underlying assets",
        "C. The ETF is correctly priced at fair market value",
        "D. The ETF's market price is irrelevant since all ETFs always trade at NAV"
      ],
      correct: 1,
      explanation: "When an ETF's market price ($50.20) exceeds its NAV ($50.00), it trades at a premium. The investor pays $0.20 more than the underlying asset value. Authorized Participants typically arbitrage this gap by creating new ETF units, but premiums and discounts are normal features of ETF trading."
    },
    {
      id: 83, element: 6, difficulty: "Hard",
      question: "An institutional investor evaluates a 3x leveraged ETF on the S&P/TSX 60 with daily ±2% volatility, a $40 market price, and a 1.5% management fee. The investor plans a one-year hold. Which factor most critically impacts the analysis?",
      options: [
        "A. The $40 entry price compared to non-leveraged alternatives",
        "B. The 1.5% management fee eroding leveraged returns over the year",
        "C. The TSX's daily volatility amplifying returns beyond the 3x target",
        "D. The 3x leverage magnifying volatility and compounding losses in fluctuating markets over the holding period"
      ],
      correct: 3,
      explanation: "The most critical factor is volatility decay (path dependency). Leveraged ETFs reset daily — in volatile, sideways markets, daily 3x amplification causes compounding losses even if the underlying index is flat over the year. This 'volatility drag' can devastate long-term returns and is far more impactful than fees or entry prices."
    },
    {
      id: 84, element: 6, difficulty: "Medium",
      question: "An Investment Dealer's Registered Representative is designing marketing materials comparing managed equity funds to individual stock selection. Which statement would be most appropriate for promotional materials?",
      options: [
        "A. Managed funds give investors maximum control over individual investment decisions",
        "B. Individual stocks minimize rebalancing costs through economies of scale",
        "C. Managed equity funds offer professional diversification and require less time commitment from the investor",
        "D. Individual stocks are the better choice for tracking broad market returns globally"
      ],
      correct: 2,
      explanation: "Managed equity funds' key marketing advantages are: (1) professional management providing expertise, (2) built-in diversification reducing single-stock risk, and (3) convenience requiring less investor time and effort. Individual stocks offer more control (not managed funds — A is wrong) and tracking market returns is an ETF/index function (D is wrong)."
    },
    {
      id: 85, element: 6, difficulty: "Hard",
      question: "A hedge fund manager wants to protect equity holdings from downside risk while maintaining upside exposure. They consider using derivatives. Which strategy best achieves this objective?",
      options: [
        "A. Buy put options on the portfolio holdings to limit downside while retaining full upside potential",
        "B. Enter a forward contract to sell holdings at a fixed price, locking in current value",
        "C. Establish a protective collar by buying puts and selling calls to reduce hedging cost",
        "D. Liquidate a portion of equities and use futures to replicate market exposure"
      ],
      correct: 0,
      explanation: "Buying protective puts provides a 'price floor' — if the stock falls below the strike price, the put compensates for losses. Crucially, the investor retains full upside if prices rise (unlike a collar that caps gains, or a forward that eliminates upside). The cost is the put premium paid."
    },
    {
      id: 86, element: 6, difficulty: "Medium",
      question: "A pension fund considers investing in a toll highway infrastructure project for inflation protection. Which statement best describes the risk/return considerations?",
      options: [
        "A. Toll highways provide guaranteed returns with no regulatory or maintenance concerns",
        "B. Infrastructure investments like toll highways can hedge inflation through escalating revenue but are illiquid, require ongoing capital expenditure, and face political/regulatory risk",
        "C. Toll highway investments are completely shielded from economic cycles",
        "D. Management outsourcing eliminates all operational risk for infrastructure investors"
      ],
      correct: 1,
      explanation: "Infrastructure investments offer inflation linkage (toll escalation clauses) and stable long-term cash flows suitable for pension fund liability matching. However, they are highly illiquid, require significant ongoing capital investment for maintenance, and face regulatory risks from government intervention in toll pricing."
    },
    {
      id: 87, element: 6, difficulty: "Medium",
      question: "A food distributor issues retractable preferred shares with a fixed 5% dividend and early redemption fees. How should an institutional investor evaluate the long-term risk/return profile?",
      options: [
        "A. The retractable feature eliminates all redemption costs, guaranteeing full principal recovery at any time",
        "B. Early redemption fees and penalties can materially reduce net returns, even when the dividend yield appears attractive",
        "C. Retractable preferred shares always outperform common shares in total return",
        "D. Early redemption fees are optional and rarely enforced by issuers"
      ],
      correct: 1,
      explanation: "While the 5% dividend is attractive, early redemption fees act as a hidden cost. If the investor needs to exit before the retraction date, these fees can significantly reduce total returns. Investors must evaluate the all-in cost including potential early exit penalties when comparing to alternative investments."
    },
    {
      id: 88, element: 6, difficulty: "Easy",
      question: "Which of the following best describes a key advantage of algorithmic trading related to its consistency?",
      options: [
        "A. It executes trades instantly to capitalize on short-term market movements",
        "B. It processes large data volumes to identify trading opportunities",
        "C. It applies trading strategies uniformly, avoiding unpredictable human emotional interventions",
        "D. It eliminates the need for manual execution, reducing operational costs"
      ],
      correct: 2,
      explanation: "The consistency advantage of algorithmic trading is that it executes strategies uniformly according to pre-programmed rules, removing human emotional biases (fear, greed, overconfidence) that can cause unpredictable deviations from the trading plan."
    },

    // ===== ELEMENT 7: Execution & Market Integrity (12 Qs) =====
    {
      id: 89, element: 7, difficulty: "Medium",
      question: "An institutional client asks an Investment Dealer to sell 10,000 shares as quickly as possible while maximizing the sale price. An ATS offers a higher bid but lower volume; the TSXV has more liquidity but a slightly lower bid. What should the dealer consider most critically?",
      options: [
        "A. The ATS's higher bid price to maximize the sale price",
        "B. The TSXV's higher liquidity to ensure faster execution",
        "C. Settlement rule differences between venues",
        "D. The size of the order compared to each venue's available liquidity to determine where full execution is achievable at the best overall price"
      ],
      correct: 3,
      explanation: "Best execution requires evaluating the order size relative to available liquidity at each venue. A higher bid price is meaningless if the ATS can only fill a fraction of the order. The dealer must assess where the full 10,000 shares can be executed at the best net price considering both price and fill probability."
    },
    {
      id: 90, element: 7, difficulty: "Medium",
      question: "Which of the following best describes how a proprietary trading desk operates within an Investment Dealer?",
      options: [
        "A. It executes client orders to earn commission fees",
        "B. It hedges client positions using market derivatives",
        "C. It trades the firm's own capital to generate direct profits for the firm",
        "D. It matches client trades with proprietary market intelligence"
      ],
      correct: 2,
      explanation: "A proprietary ('prop') trading desk uses the firm's own capital to trade for direct profit. Unlike agency or facilitation desks that serve client needs, the prop desk's sole objective is generating returns for the firm's own account. This function is distinct from client-facing trading activities."
    },
    {
      id: 91, element: 7, difficulty: "Hard",
      question: "An Investment Dealer grants Direct Electronic Access (DEA) to a hedge fund, allowing it to bypass standard order routing. Under UMIR, what is the dealer's ongoing obligation?",
      options: [
        "A. Monitor the DEA client's order flow to detect and prevent manipulative practices such as layering or spoofing",
        "B. The dealer has no further responsibility once DEA is granted to a sophisticated client",
        "C. Disclose the DEA arrangement to the exchange and let the exchange handle surveillance",
        "D. Simply require the client to sign a market integrity acknowledgment and bear no further monitoring obligation"
      ],
      correct: 0,
      explanation: "Under UMIR, the Investment Dealer that provides DEA remains fully responsible for monitoring the client's trading activity. The dealer must implement pre-trade and post-trade surveillance to detect and prevent manipulative practices (layering, spoofing, wash trades) — this obligation cannot be transferred to the client or the exchange."
    },
    {
      id: 92, element: 7, difficulty: "Medium",
      question: "A sell-side desk uses an algorithm to minimize market impact on large equity orders, but analysis shows possible over-optimization. What is the most likely negative impact?",
      options: [
        "A. Over-optimization can cause slower execution, missing favorable price windows and leaving orders partially unfilled",
        "B. The algorithm guarantees best prices regardless of optimization level",
        "C. Over-optimization eliminates price risk entirely by prioritizing speed",
        "D. Over-optimized algorithms are self-correcting and require no recalibration"
      ],
      correct: 0,
      explanation: "Over-optimized algorithms may trade too cautiously to minimize market impact, resulting in: (1) excessively slow execution that misses favorable price windows, (2) partial fills on large orders as opportunities pass, and (3) increased opportunity cost from delayed execution. The balance between impact minimization and execution speed requires ongoing calibration."
    },
    {
      id: 93, element: 7, difficulty: "Hard",
      question: "An Investment Dealer's Compliance Officer receives an anonymous tip alleging insider trading by an institutional client, consistent with rumors about an earnings leak. What should the Compliance Officer do?",
      options: [
        "A. Monitor the client's trading patterns and the trader's communications to assess potential collusion",
        "B. Immediately report the client to the regulator and suspend the account",
        "C. Ignore the tip since it is anonymous and cannot be independently confirmed",
        "D. Investigate the suspected trading activity and report to regulators if suspicions are substantiated"
      ],
      correct: 3,
      explanation: "The proper procedure is to: (1) investigate the allegation internally by reviewing trading patterns, timing relative to material events, and communications; (2) if the investigation substantiates the suspicion, report to the appropriate regulator (CIRO/securities commission). Anonymous tips cannot be ignored but also don't justify immediate reporting without investigation."
    },
    {
      id: 94, element: 7, difficulty: "Easy",
      question: "A fund manager places a market order to buy a highly liquid stock through their Investment Dealer. Which statement best describes how this order operates?",
      options: [
        "A. It fills only at or below a specified price, with no guarantee of full execution",
        "B. It seeks the best available price with no limit on price paid, ensuring immediate full execution",
        "C. It seeks instant execution but may reject partial fills if no suitable counterparties exist",
        "D. It hides the majority of the order, showing only a small visible portion to the market"
      ],
      correct: 1,
      explanation: "A market order is an instruction to execute immediately at the best available price. There is no price limit — the order will be filled at whatever price is offered. For highly liquid stocks, execution is virtually instantaneous. The trade-off is price uncertainty — in volatile markets, the execution price may differ from the last quoted price."
    },
    {
      id: 95, element: 7, difficulty: "Medium",
      question: "The current market price is $49.75. An institutional client places a sell limit order at $50.00. The price first rises to $50.10, then drops to $49.88. What should the Investment Dealer do?",
      options: [
        "A. Sell only if the price reaches exactly $50.00",
        "B. Sell at $50.10 when the price first reaches or exceeds the $50.00 limit",
        "C. Sell at $49.88 when the price drops",
        "D. Sell immediately at $49.75"
      ],
      correct: 1,
      explanation: "A sell limit order instructs the dealer to sell at the limit price OR BETTER. When the price reaches $50.10, it exceeds the $50.00 limit, triggering execution. The dealer should sell at $50.10 — the best available price above the limit. The order does not require the exact limit price."
    },
    {
      id: 96, element: 7, difficulty: "Medium",
      question: "An Investment Dealer executes a buy trade for an institutional client. On settlement day, the selling dealer fails to deliver the shares due to an administrative error. What should the buying dealer do?",
      options: [
        "A. Cancel the trade and inform the client of the settlement failure",
        "B. Extend the settlement date and attempt delivery on the next business day",
        "C. Report the failure to the clearing agency (CDS) for guidance and resolution",
        "D. Immediately purchase replacement shares on the open market at the client's expense"
      ],
      correct: 2,
      explanation: "When a settlement failure occurs, the proper procedure is to report it to the clearing agency (CDS Clearing and Depository Services). CDS has established procedures for handling failed trades, including buy-in processes where the purchasing side can acquire shares elsewhere and charge the failing party for any price difference."
    },
    {
      id: 97, element: 7, difficulty: "Medium",
      question: "An Approved Person at an Investment Dealer intends to open a personal securities trading account at a different dealer. What must they do?",
      options: [
        "A. Open the account first, then inform their employer afterward",
        "B. No disclosure is required as long as the account is marked as a non-client professional account",
        "C. Inform their own dealer, obtain written approval, and ensure the external account is disclosed before opening",
        "D. Only obtain approval from the dealer where the new account is being opened"
      ],
      correct: 2,
      explanation: "Approved Persons must disclose and obtain prior written approval from their own dealer before opening any external securities account. This requirement exists to enable the firm to monitor for potential conflicts of interest, front-running, and insider trading across all the employee's accounts."
    },
    {
      id: 98, element: 7, difficulty: "Easy",
      question: "What is an Investment Dealer required to do when an error in an institutional client's trade details is discovered after execution?",
      options: [
        "A. Correct the error and inform the client promptly",
        "B. Document the error for internal audit review at year-end",
        "C. Report the error to the regulator before informing the client",
        "D. Wait until the next account reconciliation to amend the details"
      ],
      correct: 0,
      explanation: "When a trade error is discovered, the dealer must correct it and notify the client promptly. Transparency and speed are essential — delaying correction can compound the financial impact and erode client trust. Internal documentation follows, but client notification is the immediate priority."
    },
    {
      id: 99, element: 7, difficulty: "Hard",
      question: "A sales trader misses an institutional client's call to modify a limit order, resulting in a missed market opportunity and financial loss. The client insists the trader should have been reachable. Which statement aligns with best practices?",
      options: [
        "A. Sales traders should disclose their market hours so clients know when orders can be received",
        "B. Sales traders should be continuously accessible during market hours to ensure timely order modifications, demonstrating trade accuracy and timeliness standards",
        "C. Sales traders should provide clients with direct electronic access during periods of unavailability",
        "D. Best execution obligations are satisfied at the time of original order placement only"
      ],
      correct: 1,
      explanation: "Under trade management best practices, sales traders acting as Registered Representatives should maintain continuous accessibility during market hours. The ability to receive and act on client instructions promptly — including order modifications — is fundamental to meeting the standards of trade accuracy and timeliness."
    },
    {
      id: 100, element: 7, difficulty: "Medium",
      question: "An Investment Dealer is reviewing policies mandated by Canadian regulators for debt market trading. Which statement best describes the main purpose of these regulatory requirements?",
      options: [
        "A. To reduce systemic risk and promote transparent, orderly trading in the fixed-income market",
        "B. To guarantee uniform pricing for all investors regardless of market conditions",
        "C. To limit market participation to only the largest institutional players",
        "D. To standardize all transaction fees across every issuer in the marketplace"
      ],
      correct: 0,
      explanation: "Canadian debt market regulations aim to reduce systemic risk (preventing cascading failures across interconnected institutions) and promote market transparency and orderliness. They do not guarantee uniform pricing (B), restrict access (C), or standardize fees (D) — markets remain competitive with variable pricing."
    }
  ]
};

// ======== STYLES ========
const colors = {
  bg: "#f8fafc", card: "#ffffff", text: "#0f172a", textLight: "#475569", textMuted: "#94a3b8",
  blue: "#1e40af", blueBg: "#dbeafe", green: "#166534", greenBg: "#dcfce7",
  amber: "#92400e", amberBg: "#fef3c7", red: "#991b1b", redBg: "#fee2e2",
  purple: "#5b21b6", purpleBg: "#ede9fe", teal: "#0e7490", tealBg: "#ccfbf1",
  border: "#e2e8f0", borderLight: "#f1f5f9",
};

const elementColors = [
  { color: colors.blue, bg: colors.blueBg },
  { color: colors.purple, bg: colors.purpleBg },
  { color: colors.teal, bg: colors.tealBg },
  { color: colors.green, bg: colors.greenBg },
  { color: colors.amber, bg: colors.amberBg },
  { color: colors.red, bg: colors.redBg },
  { color: colors.blue, bg: colors.blueBg },
];

// ======== COMPONENTS ========
function Timer({ timeLeft, totalTime }) {
  const mins = Math.floor(timeLeft / 60);
  const secs = timeLeft % 60;
  const pct = (timeLeft / (totalTime * 60)) * 100;
  const isLow = timeLeft < 600;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ width: 120, height: 6, background: colors.border, borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: isLow ? colors.red : colors.blue, borderRadius: 3, transition: "width 1s linear" }} />
      </div>
      <span style={{ fontFamily: "'Georgia', serif", fontSize: 15, fontWeight: 700, color: isLow ? colors.red : colors.text, minWidth: 60 }}>
        {String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}
      </span>
    </div>
  );
}

function ProgressBar({ answered, total }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ width: 120, height: 6, background: colors.border, borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${(answered / total) * 100}%`, height: "100%", background: colors.green, borderRadius: 3, transition: "width 0.3s" }} />
      </div>
      <span style={{ fontSize: 13, color: colors.textLight }}>{answered}/{total}</span>
    </div>
  );
}

function QuestionNav({ questions, answers, current, onSelect, flagged }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, maxWidth: 520 }}>
      {questions.map((q, i) => {
        const isCurrent = i === current;
        const isAnswered = answers[q.id] !== undefined;
        const isFlagged = flagged.has(q.id);
        return (
          <button key={q.id} onClick={() => onSelect(i)}
            style={{
              width: 32, height: 32, borderRadius: 6, border: isCurrent ? `2px solid ${colors.blue}` : `1px solid ${colors.border}`,
              background: isAnswered ? colors.blueBg : colors.card, color: isAnswered ? colors.blue : colors.textMuted,
              fontSize: 11, fontWeight: isCurrent ? 700 : 500, cursor: "pointer", position: "relative",
              fontFamily: "system-ui", display: "flex", alignItems: "center", justifyContent: "center"
            }}>
            {i + 1}
            {isFlagged && <span style={{ position: "absolute", top: -2, right: -2, width: 8, height: 8, borderRadius: "50%", background: colors.amber }} />}
          </button>
        );
      })}
    </div>
  );
}

function DiagnosticChart({ elementScores }) {
  const maxW = 280;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {elementScores.map((e, i) => {
        const pct = e.total > 0 ? Math.round((e.correct / e.total) * 100) : 0;
        const ec = elementColors[i % elementColors.length];
        return (
          <div key={i}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontSize: 12, color: colors.textLight, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                E{e.id}: {e.name}
              </span>
              <span style={{ fontSize: 12, fontWeight: 700, color: pct >= 60 ? colors.green : colors.red }}>{pct}% ({e.correct}/{e.total})</span>
            </div>
            <div style={{ width: "100%", maxWidth: maxW, height: 8, background: colors.borderLight, borderRadius: 4, overflow: "hidden" }}>
              <div style={{ width: `${pct}%`, height: "100%", background: pct >= 60 ? ec.color : colors.red, borderRadius: 4, transition: "width 0.5s" }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ======== MAIN APP ========
export default function ISEMockExam1() {
  const [screen, setScreen] = useState("home");
  const [mode, setMode] = useState(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState(new Set());
  const [timeLeft, setTimeLeft] = useState(EXAM_DATA.timeLimit * 60);
  const [submitted, setSubmitted] = useState(false);
  const [showNav, setShowNav] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const timerRef = useRef(null);
  const questions = EXAM_DATA.questions;
  const q = questions[currentQ];

  useEffect(() => {
    if (mode === "exam" && !submitted && screen === "quiz") {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) { clearInterval(timerRef.current); setSubmitted(true); setScreen("results"); return 0; }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timerRef.current);
    }
  }, [mode, submitted, screen]);

  const startExam = (m) => { setMode(m); setScreen("quiz"); setCurrentQ(0); setAnswers({}); setFlagged(new Set()); setTimeLeft(EXAM_DATA.timeLimit * 60); setSubmitted(false); };
  const selectAnswer = (qId, idx) => { if (submitted && mode === "exam") return; setAnswers(prev => ({ ...prev, [qId]: idx })); };
  const toggleFlag = (qId) => { setFlagged(prev => { const n = new Set(prev); n.has(qId) ? n.delete(qId) : n.add(qId); return n; }); };
  const goNext = () => { if (currentQ < questions.length - 1) setCurrentQ(currentQ + 1); };
  const goPrev = () => { if (currentQ > 0) setCurrentQ(currentQ - 1); };
  const submitExam = () => { if (timerRef.current) clearInterval(timerRef.current); setSubmitted(true); setScreen("results"); };

  const answeredCount = Object.keys(answers).length;
  const correctCount = questions.filter(q => answers[q.id] === q.correct).length;
  const score = questions.length > 0 ? Math.round((correctCount / questions.length) * 100) : 0;
  const passed = score >= EXAM_DATA.passingScore;

  const elementScores = EXAM_DATA.elements.map(el => {
    const elQs = questions.filter(q => q.element === el.id);
    const correct = elQs.filter(q => answers[q.id] === q.correct).length;
    return { ...el, correct, total: elQs.length };
  });

  // ===== HOME SCREEN =====
  if (screen === "home") {
    return (
      <div style={{ minHeight: "100vh", background: colors.bg, fontFamily: "system-ui, -apple-system, sans-serif", padding: "40px 16px" }}>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div style={{ display: "inline-block", padding: "6px 16px", background: colors.blueBg, borderRadius: 20, marginBottom: 16 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: colors.blue, letterSpacing: 1.5, textTransform: "uppercase" }}>Vitta Finance Academy</span>
            </div>
            <h1 style={{ fontFamily: "'Georgia', serif", fontSize: 36, fontWeight: 700, color: colors.text, margin: "0 0 8px" }}>
              Institutional Securities Exam
            </h1>
            <h2 style={{ fontFamily: "'Georgia', serif", fontSize: 22, fontWeight: 400, color: colors.textLight, margin: 0 }}>
              Mock Exam 1
            </h2>
          </div>

          <div style={{ background: colors.card, borderRadius: 16, border: `1px solid ${colors.border}`, padding: 28, marginBottom: 20 }}>
            <h3 style={{ fontFamily: "'Georgia', serif", fontSize: 18, color: colors.text, margin: "0 0 16px" }}>Exam Parameters</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {[["Questions", "100 Multiple Choice"], ["Duration", "2.5 Hours (150 min)"], ["Passing Score", "60%"], ["Attempts", "Unlimited (Practice)"]].map(([k, v]) => (
                <div key={k} style={{ padding: 12, background: colors.borderLight, borderRadius: 10 }}>
                  <div style={{ fontSize: 11, color: colors.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>{k}</div>
                  <div style={{ fontSize: 15, color: colors.text, fontWeight: 600, marginTop: 4 }}>{v}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: colors.card, borderRadius: 16, border: `1px solid ${colors.border}`, padding: 28, marginBottom: 28 }}>
            <h3 style={{ fontFamily: "'Georgia', serif", fontSize: 18, color: colors.text, margin: "0 0 16px" }}>Element Breakdown</h3>
            {EXAM_DATA.elements.map((el, i) => {
              const ec = elementColors[i];
              return (
                <div key={el.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: i < EXAM_DATA.elements.length - 1 ? `1px solid ${colors.borderLight}` : "none" }}>
                  <span style={{ fontSize: 14, color: colors.text }}>
                    <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: ec.color, marginRight: 8 }} />
                    E{el.id}: {el.name}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: ec.color, background: ec.bg, padding: "2px 10px", borderRadius: 10 }}>{el.count}</span>
                </div>
              );
            })}
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <button onClick={() => startExam("practice")} style={{
              flex: 1, padding: "16px 24px", background: colors.card, border: `2px solid ${colors.blue}`, borderRadius: 12, cursor: "pointer",
              fontFamily: "'Georgia', serif", fontSize: 16, fontWeight: 700, color: colors.blue
            }}>
              Practice Mode
              <div style={{ fontSize: 12, fontWeight: 400, color: colors.textLight, marginTop: 4, fontFamily: "system-ui" }}>Instant feedback after each answer</div>
            </button>
            <button onClick={() => startExam("exam")} style={{
              flex: 1, padding: "16px 24px", background: colors.blue, border: "none", borderRadius: 12, cursor: "pointer",
              fontFamily: "'Georgia', serif", fontSize: 16, fontWeight: 700, color: "#fff"
            }}>
              Exam Mode
              <div style={{ fontSize: 12, fontWeight: 400, color: "rgba(255,255,255,0.7)", marginTop: 4, fontFamily: "system-ui" }}>Timed — results at the end</div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== RESULTS SCREEN =====
  if (screen === "results") {
    return (
      <div style={{ minHeight: "100vh", background: colors.bg, fontFamily: "system-ui, -apple-system, sans-serif", padding: "40px 16px" }}>
        <div style={{ maxWidth: 700, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <div style={{ display: "inline-block", padding: "6px 16px", background: passed ? colors.greenBg : colors.redBg, borderRadius: 20, marginBottom: 12 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: passed ? colors.green : colors.red, letterSpacing: 1.5, textTransform: "uppercase" }}>
                {passed ? "PASSED" : "NOT YET PASSED"}
              </span>
            </div>
            <h1 style={{ fontFamily: "'Georgia', serif", fontSize: 48, fontWeight: 700, color: passed ? colors.green : colors.red, margin: "0 0 4px" }}>{score}%</h1>
            <p style={{ fontSize: 15, color: colors.textLight, margin: 0 }}>{correctCount} correct out of {questions.length} questions</p>
          </div>

          <div style={{ background: colors.card, borderRadius: 16, border: `1px solid ${colors.border}`, padding: 28, marginBottom: 20 }}>
            <h3 style={{ fontFamily: "'Georgia', serif", fontSize: 18, color: colors.text, margin: "0 0 20px" }}>Element Diagnostics</h3>
            <DiagnosticChart elementScores={elementScores} />
          </div>

          <div style={{ background: colors.card, borderRadius: 16, border: `1px solid ${colors.border}`, padding: 28, marginBottom: 20 }}>
            <h3 style={{ fontFamily: "'Georgia', serif", fontSize: 18, color: colors.text, margin: "0 0 16px" }}>Difficulty Breakdown</h3>
            {["Easy", "Medium", "Hard"].map(d => {
              const dQs = questions.filter(q => q.difficulty === d);
              const dCorrect = dQs.filter(q => answers[q.id] === q.correct).length;
              const pct = dQs.length > 0 ? Math.round((dCorrect / dQs.length) * 100) : 0;
              return (
                <div key={d} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0" }}>
                  <span style={{ fontSize: 14, color: colors.text }}>{d}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: pct >= 60 ? colors.green : colors.red }}>{pct}% ({dCorrect}/{dQs.length})</span>
                </div>
              );
            })}
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <button onClick={() => { setScreen("quiz"); setCurrentQ(0); setSubmitted(true); }} style={{
              flex: 1, padding: "14px 20px", background: colors.card, border: `2px solid ${colors.blue}`, borderRadius: 12,
              cursor: "pointer", fontFamily: "'Georgia', serif", fontSize: 15, fontWeight: 700, color: colors.blue
            }}>Review Answers</button>
            <button onClick={() => setScreen("home")} style={{
              flex: 1, padding: "14px 20px", background: colors.blue, border: "none", borderRadius: 12,
              cursor: "pointer", fontFamily: "'Georgia', serif", fontSize: 15, fontWeight: 700, color: "#fff"
            }}>Back to Home</button>
          </div>
        </div>
      </div>
    );
  }

  // ===== QUIZ SCREEN =====
  const isAnswered = answers[q.id] !== undefined;
  const showFeedback = (mode === "practice" && isAnswered) || (submitted);
  const selectedIdx = answers[q.id];
  const isCorrect = selectedIdx === q.correct;
  const ec = elementColors[(q.element - 1) % elementColors.length];

  return (
    <div style={{ minHeight: "100vh", background: colors.bg, fontFamily: "system-ui, -apple-system, sans-serif" }}>
      {/* Header */}
      <div style={{ background: colors.card, borderBottom: `1px solid ${colors.border}`, padding: "12px 20px", position: "sticky", top: 0, zIndex: 10 }}>
        <div style={{ maxWidth: 800, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontFamily: "'Georgia', serif", fontSize: 15, fontWeight: 700, color: colors.text }}>ISE Mock 1</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: mode === "exam" ? colors.red : colors.blue, background: mode === "exam" ? colors.redBg : colors.blueBg, padding: "2px 8px", borderRadius: 8, textTransform: "uppercase" }}>
              {mode}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <ProgressBar answered={answeredCount} total={questions.length} />
            {mode === "exam" && !submitted && <Timer timeLeft={timeLeft} totalTime={EXAM_DATA.timeLimit} />}
            <button onClick={() => setShowNav(!showNav)} style={{ padding: "6px 12px", background: colors.borderLight, border: `1px solid ${colors.border}`, borderRadius: 8, fontSize: 12, cursor: "pointer", color: colors.textLight }}>
              {showNav ? "Hide" : "Grid"}
            </button>
          </div>
        </div>
        {showNav && (
          <div style={{ maxWidth: 800, margin: "12px auto 0", paddingTop: 12, borderTop: `1px solid ${colors.borderLight}` }}>
            <QuestionNav questions={questions} answers={answers} current={currentQ} onSelect={(i) => { setCurrentQ(i); setShowNav(false); }} flagged={flagged} />
          </div>
        )}
      </div>

      {/* Question Area */}
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "28px 20px" }}>
        {/* Meta */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: ec.color, background: ec.bg, padding: "3px 10px", borderRadius: 8 }}>
            Element {q.element}
          </span>
          <span style={{ fontSize: 12, fontWeight: 600, color: q.difficulty === "Hard" ? colors.red : q.difficulty === "Medium" ? colors.amber : colors.green,
            background: q.difficulty === "Hard" ? colors.redBg : q.difficulty === "Medium" ? colors.amberBg : colors.greenBg,
            padding: "3px 10px", borderRadius: 8 }}>
            {q.difficulty}
          </span>
          <span style={{ fontSize: 13, color: colors.textMuted, marginLeft: "auto" }}>Question {currentQ + 1} of {questions.length}</span>
        </div>

        {/* Question */}
        <div style={{ background: colors.card, borderRadius: 16, border: `1px solid ${colors.border}`, padding: 28, marginBottom: 20 }}>
          <p style={{ fontFamily: "'Georgia', serif", fontSize: 17, lineHeight: 1.7, color: colors.text, margin: 0 }}>{q.question}</p>
        </div>

        {/* Options */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
          {q.options.map((opt, i) => {
            const isSelected = selectedIdx === i;
            const isCorrectOpt = i === q.correct;
            let bg = colors.card, border = colors.border, textCol = colors.text;
            if (showFeedback) {
              if (isCorrectOpt) { bg = colors.greenBg; border = colors.green; textCol = colors.green; }
              else if (isSelected && !isCorrectOpt) { bg = colors.redBg; border = colors.red; textCol = colors.red; }
            } else if (isSelected) { bg = colors.blueBg; border = colors.blue; textCol = colors.blue; }

            return (
              <button key={i} onClick={() => selectAnswer(q.id, i)} disabled={showFeedback && mode === "practice"}
                style={{
                  padding: "14px 18px", background: bg, border: `2px solid ${border}`, borderRadius: 12, cursor: showFeedback && mode === "practice" ? "default" : "pointer",
                  textAlign: "left", fontSize: 15, lineHeight: 1.6, color: textCol, fontFamily: "system-ui", transition: "all 0.2s"
                }}>
                {opt}
              </button>
            );
          })}
        </div>

        {/* Feedback */}
        {showFeedback && (
          <div style={{ background: isCorrect ? colors.greenBg : colors.redBg, borderRadius: 12, padding: 20, marginBottom: 20, border: `1px solid ${isCorrect ? colors.green : colors.red}` }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: isCorrect ? colors.green : colors.red, marginBottom: 6 }}>
              {isCorrect ? "✓ Correct" : `✗ Incorrect — Correct answer: ${String.fromCharCode(65 + q.correct)}`}
            </div>
            <p style={{ fontSize: 14, lineHeight: 1.7, color: colors.text, margin: 0 }}>{q.explanation}</p>
          </div>
        )}

        {/* Navigation */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={goPrev} disabled={currentQ === 0} style={{
              padding: "10px 20px", background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 10,
              cursor: currentQ === 0 ? "default" : "pointer", fontSize: 14, fontWeight: 600, color: currentQ === 0 ? colors.textMuted : colors.text, opacity: currentQ === 0 ? 0.5 : 1
            }}>← Previous</button>
            <button onClick={goNext} disabled={currentQ === questions.length - 1} style={{
              padding: "10px 20px", background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 10,
              cursor: currentQ === questions.length - 1 ? "default" : "pointer", fontSize: 14, fontWeight: 600, color: currentQ === questions.length - 1 ? colors.textMuted : colors.text, opacity: currentQ === questions.length - 1 ? 0.5 : 1
            }}>Next →</button>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {!submitted && (
              <button onClick={() => toggleFlag(q.id)} style={{
                padding: "10px 16px", background: flagged.has(q.id) ? colors.amberBg : colors.card, border: `1px solid ${flagged.has(q.id) ? colors.amber : colors.border}`,
                borderRadius: 10, cursor: "pointer", fontSize: 13, color: flagged.has(q.id) ? colors.amber : colors.textLight
              }}>
                {flagged.has(q.id) ? "★ Flagged" : "☆ Flag"}
              </button>
            )}
            {mode === "exam" && !submitted && (
              <button onClick={() => setShowConfirm(true)} style={{
                padding: "10px 20px", background: colors.blue, border: "none", borderRadius: 10, cursor: "pointer", fontSize: 14, fontWeight: 700, color: "#fff"
              }}>Submit Exam</button>
            )}
            {submitted && (
              <button onClick={() => setScreen("results")} style={{
                padding: "10px 20px", background: colors.blue, border: "none", borderRadius: 10, cursor: "pointer", fontSize: 14, fontWeight: 700, color: "#fff"
              }}>View Results</button>
            )}
          </div>
        </div>
      </div>

      {/* Submit Confirmation Modal */}
      {showConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
          <div style={{ background: colors.card, borderRadius: 16, padding: 32, maxWidth: 420, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
            <h3 style={{ fontFamily: "'Georgia', serif", fontSize: 20, color: colors.text, margin: "0 0 12px" }}>Submit Exam?</h3>
            <p style={{ fontSize: 15, color: colors.textLight, lineHeight: 1.6, margin: "0 0 24px" }}>
              You have answered <strong style={{ color: colors.text }}>{answeredCount}</strong> of <strong style={{ color: colors.text }}>{questions.length}</strong> questions.
              {answeredCount < questions.length && <span style={{ display: "block", marginTop: 8, color: colors.amber, fontWeight: 600 }}>
                {questions.length - answeredCount} question{questions.length - answeredCount > 1 ? "s" : ""} unanswered — unanswered questions will be marked incorrect.
              </span>}
            </p>
            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={() => setShowConfirm(false)} style={{ flex: 1, padding: "12px 20px", background: colors.card, border: `2px solid ${colors.border}`, borderRadius: 10, cursor: "pointer", fontSize: 14, fontWeight: 600, color: colors.textLight }}>
                Go Back
              </button>
              <button onClick={() => { setShowConfirm(false); submitExam(); }} style={{ flex: 1, padding: "12px 20px", background: colors.blue, border: "none", borderRadius: 10, cursor: "pointer", fontSize: 14, fontWeight: 700, color: "#fff" }}>
                Confirm Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
